import { DateTime } from "luxon";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { MyPreventiveActionsSortOrderType, RedirectTo } from "@/Common/Models/UI/Enums";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ScreeningTestStatus } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { PreventiveActivitiesTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { MyPreventiveActivityDto } from "@/Portfolio/Models/MyPreventiveActivityDto";
import { IPreventiveActivitiesInboxModel } from "@/Portfolio/Models/UI/IPreventiveActivitiesInboxModel";
import { PortfolioSaveOptionKey } from "./PortfolioSaveOptionKey";
import { PreventiveActivitiesAttachmentsPopup } from "./PreventiveActivitiesAttachmentsPopup";
import { PreventiveActivitiesSettingsContainer } from "./PreventiveActivitiesSettingsContainer";

import "./PreventiveActivitiesInbox.css";

import img_icon_attachment_icon from "@/Globals/_images/icons/attachment_icon.png";

interface IPreventiveActivitiesInbox {
    model: IPreventiveActivitiesInboxModel;
}

class PreventiveActivitiesInboxState {
    public popupX: number;
    public popupY: number;
    public selectedAttachments: AttachedObjectResponse[];

    public constructor(fields?: Partial<PreventiveActivitiesInboxState>) {
        Object.assign(this, fields);
    }

    public static getDefault(): PreventiveActivitiesInboxState {
        return new PreventiveActivitiesInboxState({
            popupX: 0,
            popupY: 0,
            selectedAttachments: []
        });
    }
}

class PreventiveActivitiesInbox extends BaseReactComponent<IPreventiveActivitiesInbox, PreventiveActivitiesInboxState> {
    private helper = cacheStore.Helper;

    public constructor(props: IPreventiveActivitiesInbox) {
        super(props);
        this.state = PreventiveActivitiesInboxState.getDefault();
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public render(): JSX.Element {
        const saveOptionKey = PortfolioSaveOptionKey.PreventiveActivities;
        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.defaultTableOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = MyleTableHelper.getDefaultRenderOptions(columnsOptions.toArray());
        const columnConfigModel = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="workspace">
                {this.state.selectedAttachments?.any() && (
                    <PreventiveActivitiesAttachmentsPopup
                        attachments={this.state.selectedAttachments}
                        onClosed={() => this.closeDisplayItemsPopup()}
                        id="PreventiveActivitiesAttachmentsPopup"
                        positionLeft={this.state.popupX}
                        positionTop={this.state.popupY}
                        onAttachmentClick={attachment => this.onAttachmentClick(attachment)}
                    />
                )}
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("MyPreventiveItemsLabel")}
                    searchText={this.props.model.get("SearchText")}
                    searchPlaceholder={cacheStore.GetString("SearchLabel")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveActivitiesInbox_LoadFromSearch, { searchText: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveActivitiesInbox_LoadFromSearch, { searchText: this.props.model.get("SearchText") })}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveActivitiesInbox_SettingsContainerVisibility)}
                />
                <div id="posref" className="ws-scrollable">
                    {this.props.model.get("IsSettingsContainerOpen") && (
                        <PreventiveActivitiesSettingsContainer
                            footerClassName="preventiveactivitiessettings-footer"
                            onOkCallback={state => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveActivitiesInbox_LoadSettingsSearch, { settings: state }, 200)}
                            savedState={this.props.model.get("SavedSettingsContainerState")}
                        />
                    )}
                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="PreventiveActivitiesInbox_main_tb"
                            Columns={tableColumns}
                            DataSource={Immutable.List(this.props.model.get("PreventiveActivities"))}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickTarget={MyleTableTarget.Myle_PreventiveActivities_RowClick}
                            SaveOptionskeyTarget={saveOptionKey}
                            ColumnSortTarget={MyleTableTarget.Myle_PortfolioPreventiveActivities_ColumnSortClick}
                            IsRowLocked={(item: PreventiveActivitiesTableItem) => item.Model.IsAccessProhibited}
                            TableDataCy="portfolio-table"
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.getPageSize()}
                                TotalRows={this.props.model.get("TotalQueryRows")}
                                CurrentPage={this.props.model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Portfolio.PreventiveActivitiesInbox_SetPage}
                            />
                        </div>
                    </div>
                </div>
                {this.props.model.get("IsColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfigModel}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        closeColumnOrderPopup={() => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveActivitiesInbox_ColumnOrderingPopupVisibility, { isOpen: false })}
                    />
                )}
            </div>
        );
    }

    private getPageSize(): number {
        return this.props.model.get("SavedSettingsContainerState")?.pageSize ?? cacheStore.getDefaultPageSize();
    }

    private labels = {
        Patient: "PatientLabel",
        RequestingStaff: "RequestingStaffLabel",
        Item: "ItemLabel",
        Contributors: "ContributorsDLabel",
        DueDate: "DueDateLabel",
        Status: "StatusLabel",
        UpcomingAppointment: "UpcomingApptBannerLabel",
        Attachments: "AttachmentsLabel"
    };

    private fieldNames = {
        Patient: "Patient",
        RequestingStaff: "RequestingStaff",
        Item: "Item",
        Contributors: "Contributors",
        DueDate: "DueDate",
        Status: "Status",
        UpcomingAppointment: "UpcomingAppointment",
        Attachments: "Attachments"
    };

    private defaultTableOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Patient),
                FieldName: this.fieldNames.Patient,
                RowClickEnabled: true,
                WidthPercentageDefault: 10,
                IsSortable: true,
                SortColumn: MyPreventiveActionsSortOrderType.Patient,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === MyPreventiveActionsSortOrderType.Patient
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Item),
                FieldName: this.fieldNames.Item,
                RowClickEnabled: true,
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.DueDate),
                FieldName: this.fieldNames.DueDate,
                RowClickEnabled: true,
                WidthPercentageDefault: 10,
                IsSortable: true,
                SortColumn: MyPreventiveActionsSortOrderType.DueDate,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === MyPreventiveActionsSortOrderType.DueDate
            }),
            cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff
                ? new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString(this.labels.RequestingStaff),
                    FieldName: this.fieldNames.RequestingStaff,
                    RowClickEnabled: true,
                    WidthPercentageDefault: 10
                })
                : new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString(this.labels.Contributors),
                    FieldName: this.fieldNames.Contributors,
                    RowClickEnabled: true,
                    WidthPercentageDefault: 10
                }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Status),
                FieldName: this.fieldNames.Status,
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: MyPreventiveActionsSortOrderType.Status,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === MyPreventiveActionsSortOrderType.Status
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.UpcomingAppointment),
                FieldName: this.fieldNames.UpcomingAppointment,
                RowClickEnabled: true,
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Attachments),
                FieldName: this.fieldNames.Attachments,
                RowClickEnabled: true,
                WidthPercentageDefault: 10
            })
        ];
    }

    private getTableColumns(tableOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<PreventiveActivitiesTableItem>(
            tableOptions,
            column => {
                switch (column.FieldName) {
                    case this.fieldNames.Patient:
                        return {
                            renderCell: item => (
                                item.Model.IsAccessProhibited
                                    ? <div>{cacheStore.GetString("Allowlist_BlockedLabel")}</div>
                                    : (
                                        <div>
                                            <a onClick={e => this.onPatientClick(e, item.Model)}>
                                                {NameHelper.formatFullNameForHasPatientName(item.Model)}
                                            </a>
                                        </div>
                                    )
                            )
                        };

                    case this.fieldNames.RequestingStaff:
                        return {
                            renderCell: item => {
                                const requestingStaff = cacheStore.GetEmployeeByContactFacetId(item.Model.RequestingStaffContactFacetId);

                                if (requestingStaff) {
                                    const requestingStaffTitle = cacheStore.GetLookupById(requestingStaff.PrefixLookupId);

                                    return (item.Model.IsAccessProhibited
                                        ? <div>{cacheStore.GetString("Allowlist_BlockedLabel")}</div>
                                        : <div className={this.getUrgentClassName(item)}>{`${requestingStaffTitle?.GetValue() ?? ""} ${requestingStaff.LastName}, ${requestingStaff.FirstName}`}</div>
                                    );
                                }
                            }
                        };

                    case this.fieldNames.Item:
                        return {
                            renderCell: item => item.Model.IsAccessProhibited
                                ? <div>{cacheStore.GetString("Allowlist_BlockedLabel")}</div>
                                : <div className={this.getUrgentClassName(item)}>{item.Model.ScreeningTestName}</div>
                        };

                    case this.fieldNames.Contributors:
                        return {
                            renderCell: item => <div className={this.getUrgentClassName(item)}>{item.Model.Contributors?.map(p => <div key={p}>{p}</div>)}</div>
                        };

                    case this.fieldNames.DueDate:
                        return {
                            renderCell: item => (
                                <div className={this.getUrgentClassName(item)}>
                                    {DateHelper.formatDate(item.Model.DueDate, "YYYY/MM/DD")}
                                </div>
                            )
                        };

                    case this.fieldNames.Status:
                        return {
                            renderCell: item => (
                                item.Model.IsAccessProhibited
                                    ? <div>{cacheStore.GetString("Allowlist_BlockedLabel")}</div>
                                    : (
                                        <div className={this.getUrgentClassName(item)}>
                                            {this.translateStatus(item.Model.StatusLookupId)}
                                        </div>
                                    )
                            )
                        };

                    case this.fieldNames.UpcomingAppointment:
                        return {
                            renderCell: item => (
                                item.Model.IsAccessProhibited
                                    ? <div>{cacheStore.GetString("Allowlist_BlockedLabel")}</div>
                                    : (
                                        <div className={this.getUrgentClassName(item)}>
                                            {item.Model.UpcomingAppointmentName}
                                        </div>
                                    )
                            )
                        };

                    case this.fieldNames.Attachments:
                        return {
                            renderCell: item => (
                                item.Model.IsAccessProhibited
                                    ? <div>{cacheStore.GetString("Allowlist_BlockedLabel")}</div>
                                    : (
                                        <div className={this.getUrgentClassName(item)}>
                                            {item.Model.Attachments?.any() && (
                                                <a onClick={e => this.openAttachmentPopup(item.Model, e.clientX, e.clientY)}>
                                                    <img src={img_icon_attachment_icon} className="hover paperClip-rotation" />
                                                </a>
                                            )}
                                        </div>
                                    )
                            )
                        };
                }
            },
            false
        );
    }

    private getUrgentClassName(item: PreventiveActivitiesTableItem): string {
        return item.Model.DueDate && DateTime.local() > DateTime.fromISO(item.Model.DueDate.toString())
            ? "red-text"
            : "";
    }

    private translateStatus(statusLookupId: string): string {
        return ScreeningTestStatus
            .GetAll()
            .firstOrDefault(p => p.LookupID === statusLookupId)?.GetValue() ?? "";
    }

    private openAttachmentPopup(item: MyPreventiveActivityDto, clientX: number, clientY: number): void {
        if (item.Attachments.length === 1)
            this.onAttachmentClick(item.Attachments[0]);
        else {
            this.mutateState(
                state => {
                    state.popupX = clientX - 350;
                    state.popupY = clientY;
                    state.selectedAttachments = item.Attachments;
                }
            );
        }
    }

    private closeDisplayItemsPopup(): void {
        this.mutateState(
            state => {
                state.selectedAttachments = [];
            }
        );
    }

    private onPatientClick(e: React.MouseEvent<HTMLAnchorElement>, item: MyPreventiveActivityDto): void {
        e.stopPropagation();
        window.setTimeout(() => {
            sessionStore.dispatch(
                LocalOperations.Misc.RedirectToTab,
                {
                    value: item.PatientIndividualId,
                    target: RedirectTo.Patient,
                    currentLocation: RouterHistory.location,
                    encounterEdit: false
                }
            );
        }, 100);
    }

    private onAttachmentClick(attachment: AttachedObjectResponse): void {
        ExportableFileHelper.fromAttachedObject(attachment).openInNewWindow();
    }
}

export { PreventiveActivitiesInbox };
